import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useMemo, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Plus, Edit2, Trash2, Upload, Search } from 'lucide-react';
import {
  createUrithiDummyAsset, deleteUthiriDummyAsset, fetchUrithiDummyAssets,
  IMG_BASE_URL, updateUrithiDummyAsset,
} from './hooks/hooks';
import {
  Toast, LoadingState, ErrorState, Pagination, BackButton, ConfirmModal,
} from './ui';

const validationSchema = Yup.object({
  name: Yup.string().max(255).required('Required'),
  description: Yup.string().min(100, 'Description must be at least 100 characters').required('Required'),
  price: Yup.number().min(0).required('Required'),
  stock: Yup.number().positive().integer().required('Required'),
  image: Yup.mixed().required('Image is required'),
});

const upValidationSchema = Yup.object({
  name: Yup.string().max(255).required('Required'),
  description: Yup.string().min(100).required('Required'),
  price: Yup.number().min(0).required('Required'),
  stock: Yup.number().positive().integer().required('Required'),
});

const inputCls = 'w-full px-3.5 py-2.5 border border-gray-200 text-sm rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 transition-all bg-white placeholder:text-gray-400';

const Field = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-bold text-[#001f3f] uppercase tracking-wide block">{label}</label>
    {children}
    {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
  </div>
);

function UrithiDummyAssets() {
  const queryClient = useQueryClient();
  const { data: assets, isLoading, isError, refetch } = useQuery({
    queryKey: ['urithiDummyAssets'],
    queryFn: fetchUrithiDummyAssets,
  });

  const [open, setOpen] = useState({ create: false, update: false });
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  const filteredAssets = useMemo(() =>
    Array.isArray(assets)
      ? assets.filter((item) => item.name?.toLowerCase().includes(searchQuery.toLowerCase()))
      : []
  , [assets, searchQuery]);

  const mutation = useMutation({
    mutationFn: createUrithiDummyAsset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['urithiDummyAssets'] });
      setOpen({ create: false, update: false });
      setToast({ open: true, message: 'Product added successfully!', severity: 'success' });
      formik.resetForm();
      refetch();
    },
    onError: () => setToast({ open: true, message: 'Failed to add product.', severity: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: updateUrithiDummyAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(['urithiDummyAssets']);
      setOpen({ create: false, update: false });
      setToast({ open: true, message: 'Product updated successfully!', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to update product.', severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUthiriDummyAsset,
    onSuccess: () => {
      queryClient.invalidateQueries(['urithiDummyAssets']);
      setDeleteConfirm({ open: false, id: null });
      setToast({ open: true, message: 'Product deleted successfully!', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to delete product.', severity: 'error' }),
  });

  const formik = useFormik({
    initialValues: { name: '', description: '', image: null, price: '', stock: '' },
    validationSchema,
    onSubmit: (values) => mutation.mutate(values),
  });

  const upformik = useFormik({
    initialValues: { name: '', description: '', image: null, price: '', stock: '' },
    validationSchema: upValidationSchema,
    onSubmit: (values) => {
      if (selectedItem?.id) updateMutation.mutate({ id: selectedItem.id, data: values });
    },
  });

  const handleOpenUpdate = useCallback((item) => {
    setSelectedItem(item);
    upformik.setValues({
      name: item.name || '',
      description: item.description || '',
      image: null,
      price: item.price || '',
      stock: item.stock || '',
    });
    setOpen({ create: false, update: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState onRetry={refetch} />;

  if (open.create || open.update || assets?.length === 0) {
    const isUpdate = open.update;
    const fk = isUpdate ? upformik : formik;
    const showBack = open.create || open.update;

    return (
      <div>
        <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
        {showBack && <BackButton onClick={() => setOpen({ create: false, update: false })} label="Back to Products" />}

        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
          <div className="mb-6">
            <h2 className="text-base font-bold text-[#001f3f] uppercase tracking-wide mb-1">
              {isUpdate ? 'Update Product' : (assets?.length === 0 ? 'Create Your First Product' : 'Add New Product')}
            </h2>
            <p className="text-xs text-gray-500">Fill in the details below</p>
          </div>

          <form onSubmit={fk.handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Product Name" error={fk.touched.name && fk.errors.name}>
                <input type="text" name="name" value={fk.values.name} onChange={fk.handleChange} onBlur={fk.handleBlur} className={inputCls} placeholder="Product name" />
              </Field>

              <Field label="Price (TZS)" error={fk.touched.price && fk.errors.price}>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-xs text-gray-500 font-bold">TZS</span>
                  <input type="number" name="price" value={fk.values.price} onChange={fk.handleChange} onBlur={fk.handleBlur} className={`${inputCls} pl-12`} placeholder="0.00" />
                </div>
              </Field>

              <Field label="Stock" error={fk.touched.stock && fk.errors.stock}>
                <input type="number" name="stock" value={fk.values.stock} onChange={fk.handleChange} onBlur={fk.handleBlur} className={inputCls} placeholder="Available units" />
              </Field>
            </div>

            <Field label="Description" error={fk.touched.description && fk.errors.description}>
              <textarea name="description" value={fk.values.description} onChange={fk.handleChange} onBlur={fk.handleBlur} rows={5} className={`${inputCls} resize-y`} placeholder="Detailed product description (min 100 characters)" />
            </Field>

            <Field label="Product Image" error={fk.touched.image && fk.errors.image}>
              <label className="block border-2 border-dashed border-gray-200 rounded-2xl p-8 cursor-pointer hover:border-[#001f3f] hover:bg-[#001f3f]/5 transition-all text-center">
                <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-[#001f3f]/10 mb-3">
                  <Upload className="w-6 h-6 text-[#001f3f]" />
                </div>
                <p className="text-sm font-semibold text-[#001f3f] mb-1">Click to upload image</p>
                <p className="text-xs text-gray-500">JPEG, PNG or GIF</p>
                <input type="file" className="hidden" name="image" onChange={(e) => fk.setFieldValue('image', e.currentTarget.files[0])} onBlur={fk.handleBlur} accept="image/*" />
              </label>
              {fk.values.image && (
                <div className="mt-3 p-3 bg-white rounded-xl border border-gray-200">
                  <p className="text-xs font-semibold text-gray-600 mb-2 truncate">📎 {fk.values.image.name}</p>
                  <img src={URL.createObjectURL(fk.values.image)} alt="Preview" className="max-h-64 w-full object-cover rounded-lg" />
                </div>
              )}
            </Field>

            <button type="submit" disabled={mutation.isLoading || updateMutation.isLoading} className="w-full bg-[#001f3f] hover:bg-[#0a3260] text-white py-3.5 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
              {(mutation.isLoading || updateMutation.isLoading) ? 'Processing…' : (isUpdate ? 'Update Product' : 'Add Product')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
      <ConfirmModal
        open={deleteConfirm.open}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        confirmLabel="Yes, Delete"
        danger
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
      />

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold text-[#001f3f] uppercase tracking-wide">Tradable Products</h2>
              <button onClick={() => setOpen({ create: true, update: false })} className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-3.5 py-2 rounded-full text-xs font-bold transition-colors">
                <Plus className="w-3.5 h-3.5" />
                Add Product
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search products…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-64 bg-gray-50" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Product', 'Stock', 'Price (TZS)', 'Description', 'Published', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAssets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={`${IMG_BASE_URL}${asset.image}`} alt={asset.name} className="w-10 h-10 rounded-xl object-cover border border-gray-200 bg-gray-50" />
                      <p className="text-xs font-bold text-[#001f3f] truncate max-w-[180px]">{asset.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold ${asset.stock > 10 ? 'text-emerald-600' : asset.stock > 0 ? 'text-amber-600' : 'text-red-600'}`}>{asset.stock}</span>
                  </td>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900">{Number(asset.price)?.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">{asset.description}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(asset.published_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleOpenUpdate(asset)} className="p-2 rounded-lg text-[#001f3f] hover:bg-[#001f3f]/10 transition-colors">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setDeleteConfirm({ open: true, id: asset.id })} className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} rowsPerPage={rowsPerPage} total={filteredAssets.length} onPageChange={setPage} onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }} />
      </div>
    </div>
  );
}

export default UrithiDummyAssets;
// import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// import React, { useState } from 'react'
// import { Alert, Avatar, Box, Button, Container, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
// import { useFormik } from 'formik';
// import * as Yup from 'yup';
// import { Add, ArrowBack, CloudUpload, Delete, Edit } from '@mui/icons-material';
// import { createUrithiDummyAsset, deleteUthiriDummyAsset, fetchUrithiDummyAssets, IMG_BASE_URL, updateUrithiDummyAsset } from './hooks/hooks';


// // Validation Schema using Yup
// const validationSchema = Yup.object({
//     name: Yup.string()
//         .max(255, "Must be 255 characters or less")
//         .required("Required"),
//     description: Yup.string()
//         .min(100, "Description must be at least 50 characters")
//         .required("Required"),
//     price: Yup.number()
//         .min(0, "Price must be positive")
//         .required("Required"),
//     stock: Yup.number()
//         .positive("Must be positive")
//         .integer()
//         .required("Required"),
//     image: Yup.mixed()
//       .required('Image is required')
//       .test('fileType', 'Only image files are allowed', (value) => {
//         if (value) {
//           return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
//         }
//         return true;
//       }),
//   });

//   const upValidationSchema = Yup.object({
//     name: Yup.string()
//         .max(255, "Must be 255 characters or less")
//         .required("Required"),
//     description: Yup.string()
//         .min(100, "Description must be at least 50 characters")
//         .required("Required"),
//     price: Yup.number()
//         .min(0, "Price must be positive")
//         .required("Required"),
//     stock: Yup.number()
//         .positive("Must be positive")
//         .integer()
//         .required("Required"),
// });

// function UrithiDummyAssets() {
//   const queryClient = useQueryClient(); // React Query fetch hook
//     const { data: assets, isLoading, isError, refetch } = useQuery({
//        queryKey: ["uvunajiDummyAssets"],
//        queryFn: fetchUrithiDummyAssets,
//    }); 

//    const [open, setOpen] = useState({ create: false, update: false, detail: false });
//    const [toast, setToast] = useState({ open: false, message: "", severity: "" }); 
//    const [selectedItem, setSelectedItem] = useState(null);
//    const [searchQuery, setSearchQuery] = useState("");
//    const [sortBy, setSortBy] = useState("date"); // Default sorting by date
//    const [page, setPage] = useState(0);
//    const [rowsPerPage, setRowsPerPage] = useState(5);
   
//    // Ensure news is an array before filtering
//    const filteredAssets = Array.isArray(assets) ? assets?.filter(item =>
//        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
//    ) : [];
   
   
//    // Sort news dynamically
//    const sortedNews = [...filteredAssets].sort((a, b) => {
//        if (sortBy === "name") {
//            return a.name.localeCompare(b.name);
//        } else {
//            return new Date(b.published_at) - new Date(a.published_at); // Latest first
//        }
//    });
   
//    // Handle Pagination
//    const handleChangePage = (_, newPage) => setPage(newPage);
//    const handleChangeRowsPerPage = (event) => {
//        setRowsPerPage(parseInt(event.target.value, 10));
//        setPage(0);
//    };

//    const mutation = useMutation({
//        mutationFn: createUrithiDummyAsset, //Ensure `createNews` is a function
//        onSuccess: (response) => {
//          queryClient.invalidateQueries({ queryKey: ["newsUrithiDummyAsset"] });// Uncomment if needed
//          //setFormData({ title: "", content: "", image: null });
//          setOpen({ create: false, update: false, detail:false });
//          setToast({ open: true, message: "New Asset added successfully!", severity: "success" });
//          refetch()
//        },
//        onError: (error) => {
//          setToast({ open: true, message: "Failed to add new Asset. Try again.", severity: "error" });
//        }
//      });

//    const formik = useFormik({
//      initialValues: {
//          name:"",
//          description:"",
//          image:null,
//          price:"",
//          stock:""
//      },
//      validationSchema: validationSchema,
//      onSubmit: (values) => {
//          mutation.mutate(values);
//      },
//    });
//       // Update News Mutation
//        const updateMutation = useMutation({
//            mutationFn: updateUrithiDummyAsset,
//            onSuccess: () => {
//                queryClient.invalidateQueries(["urithiAssetDummyUpdate"]);
//                // setFormDataEdit({ title: "", content: "", image: null });
//                setOpen({ create: false, update: false, detail:false });
//                setToast({ open: true, message: "Asset updated successfully!", severity: "success" });
//            },
//            onError: (error) => {
//              setToast({ open: true, message: "Failed to update Asset. Try again.", severity: "error" });
//            }
//        });
   
//        // Delete News Mutation
//        const deleteMutation = useMutation({
//            mutationFn: deleteUthiriDummyAsset,
//            onSuccess: () => {
//                queryClient.invalidateQueries(["urithiAssetDummyDelete"]);
//                setToast({ open: true, message: "Asset deleted successfully!", severity: "success" });
//            },
//            onError: (error) => {
//                setToast({ open: true, message: "Failed to delete asset. Try again.", severity: "error" });
//            }
//        });
   
//        const upformik = useFormik({
//            initialValues: {
//               name:"",
//               description:"",
//               image:null,
//               price:"",
//               stock:""
//              },
//              validationSchema: upValidationSchema,
//              onSubmit: (values) => { 
//                if (selectedItem.id) {
//                updateMutation.mutate({ id: selectedItem.id, data: values });
//              }
//              },
//            });
   
//    // Open drawer and set form data when updating
//      const handleOpenUpdateDrawer = (item) => {
//        setSelectedItem(item);
//        // Update formik values dynamically
//      upformik.setValues({
//          name:item.name || "",
//          description:item.description||"",
//          image:null,
//          price:item.price ||"",
//          stock:item.stock ||""
       
//      });
//        setOpen({ create: false, update: true });
//      };
//      const handleOpenDetailsDrawer = (item) => {
//        setSelectedItem(item);
//        setOpen({ create: false, update: false, detail:true });
//      };
       
//      // Function to toggle create drawer
//      const toggleDrawer = (type, state) => () => {
//        setOpen({ create: type === "create" ? state : false, update: type === "update" ? state : false });
       
//      };
   
     
//        // Handle delete
//        const handleDelete = (id) => {
//            deleteMutation.mutate(id);
//        };

//     if (isLoading) {
//             return (
//                 <Container sx={{ textAlign: "center", marginTop: 5 }}>
//                     <Typography>Loading...</Typography>
//                 </Container>
//             );
//         }
    
//     if (isError) {
//         return (
//             <Container sx={{ textAlign: "center", marginTop: 5 }}>
//                 <Typography variant="h6" color="error">
//                     Error fetching news. Please try again later.
//                 </Typography>
//             </Container>
//         );
//     }
//   return (
//     <Container>
//       {assets?.length === 0 ?(
//         <Container maxWidth="md">
//           <Box
//               sx={{
//                   mt: "4px",
//                   p: "4px",
//                   // borderRadius: 2,
//                   boxShadow: 0,
//                   backgroundColor: 'background.paper',
//               }}
//               >
//               <Typography variant="h4" sx={{ fontWeight: 700, color: "#001f3f", fontSize:12, textTransform:"uppercase" }}>
//                   Add New Asset
//               </Typography>
//               <Typography variant="body1" sx={{mb:2, color: "text.secondary", fontSize: 10, textTransform:"capitalize" }}>
//                   Fill in the details below to register a new asset, including name, image, and other asset details.
//               </Typography>
//               <form onSubmit={formik.handleSubmit}>
//                   <Grid container spacing={3}>
//                   {/* Title Field */}
//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       size="small"
//                       label="Asset Name"
//                       variant="outlined"
//                       name="name"
//                       value={formik.values.name}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.name && Boolean(formik.errors.name)}
//                       helperText={formik.touched.name && formik.errors.name}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
                      
//                       }}
//                       />
//                   </Grid>
      
//                   {/* Content Field */}
//                   <Grid item xs={12}>
//                   <TextField
//                     fullWidth
//                     multiline
//                     size="small"
//                     rows={4}
//                     label="Description"
//                     name="description"
//                     value={formik.values.description}
//                     onChange={formik.handleChange}
//                     onBlur={formik.handleBlur}
//                     error={formik.touched.description && Boolean(formik.errors.description)}
//                     helperText={formik.touched.description && formik.errors.description}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                               borderRadius: "2px",
//                               "&.Mui-focused fieldset": {
//                                   borderColor: "#001f3f",
//                                   borderRadius: "2px"
//                               }
//                           },
//                           '& .MuiFormHelperText-root': {
//                               fontSize: 9,
//                               color: 'error.main',
//                               textTransform: "capitalize"
//                           }
//                       }}
                     
//                  />
//                   </Grid>

//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       size="small"
//                       type="number"
//                       InputProps={{
//                         startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
//                       }}
//                       label="Price"
//                       name="price"
//                       value={formik.values.price}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.price && Boolean(formik.errors.price)}
//                       helperText={formik.touched.price && formik.errors.price}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
                      
//                       }}
//                       />
//                   </Grid>

//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       size="small" 
//                       label="Stock"
//                       name="stock"
//                       type="number"
//                       value={formik.values.stock}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.stock && Boolean(formik.errors.stock)}
//                       helperText={formik.touched.stock && formik.errors.stock}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
                      
//                       }}
//                       />
//                   </Grid> 
      
//                   {/* Image Upload Field */}
//                   <Grid item xs={12}>
//                       <Button
//                       variant="contained"
//                       component="label"
//                       sx={{
//                           color:"black",
//                           fontSize:10,
//                           fontWeight:500,
//                           borderRadius: '2px',
//                           textTransform: 'none',
//                           boxShadow:"none",
//                           py: 1,
//                           px: 3,
//                           border:"1px solid #ccc",
//                           // background:"none"
//                           backgroundColor:"#fff",                     
//                           "&:hover": {
//                               boxShadow: "none",
//                           },
//                       }}
//                       >
//                           <Box sx={{
//                               alignItems:"center",
//                               justifyContent:"center",
//                               display:"flex",
//                               flexDirection:"column"
//                           }}>
//                               <CloudUpload color="gray" />
//                               <Typography sx={{
//                                   fontSize:10,
//                                   fontWeight:500,
//                                   textTransform:"capitalize"
//                               }}>Click to upload</Typography>
//                               <Typography sx={{
//                                   fontSize:10,
//                                   fontWeight:400,
//                                   color:"#bcbcbc"
//                               }}>JPEG, PNG or GIF</Typography>
//                               <input
//                                   type="file"
//                                   hidden
//                                   name="image"
//                                   onChange={(event) => {
//                                   formik.setFieldValue('image', event.currentTarget.files[0]);
//                                   }}
//                                   onBlur={formik.handleBlur}
//                                   accept="image/*"
//                               />
//                           </Box>
                      
//                       </Button>
//                       {formik.touched.image && formik.errors.image && (
//                       <Typography variant="body2" color="error" sx={{ mt: 1, fontSize:10, textTransform:"capitalize" }}>
//                           {formik.errors.image}
//                       </Typography>
//                       )}
//                       {formik.values.image && (
//                       <Box sx={{ mt: 2 }}>
//                           <Typography variant="body2" sx={{ mb: 1, fontSize:10, }}>
//                           SELECTED FILE: {formik.values.image.name}
//                           </Typography>
//                           <img
//                           src={URL.createObjectURL(formik.values.image)}
//                           alt="Preview"
//                           style={{
//                               maxWidth: '100%',
//                               height: 'auto',
//                               maxHeight:400,
//                               borderRadius: '2px',
//                               boxShadow: 0,
//                           }}
//                           />
//                       </Box>
//                       )}
//                   </Grid>
      
//                   {/* Submit Button */}
//                   <Grid item xs={12}>
//                       <Button
//                       type="submit"
//                       variant="contained"
//                       fullWidth
//                       size="large"
//                       sx={{
//                           borderRadius: "2px",
//                           textTransform: 'none',
//                           py: 1.5,
//                           fontSize: 10,
//                           boxShadow:0,
//                           backgroundColor:"#001f3f",                  
//                           "&:hover": {
//                               boxShadow: "none",
//                           },
//                       }}
//                       >
//                       Add New Asset
//                       </Button>
//                   </Grid>
//                   </Grid>
//               </form>
//               </Box>
//             </Container>
//       ):(open.create ? (
//         <Container maxWidth="md">
//             <IconButton
//                 onClick={toggleDrawer("create", false)}
//             >
//                 <ArrowBack/>
//             </IconButton>
//         <Box
//             sx={{
//                 mt: "4px",
//                 p: "4px",
//                 // borderRadius: 2,
//                 boxShadow: 0,
//                 backgroundColor: 'background.paper',
//             }}
//             >
//             <Typography variant="h4" sx={{ fontWeight: 700, color: "#001f3f", fontSize:12, textTransform:"uppercase" }}>
//                 Add New Asset
//             </Typography>
//             <Typography variant="body1" sx={{mb:2, color: "text.secondary", fontSize: 10, textTransform:"capitalize" }}>
//                 Fill in the details below to register a new asset, including name, image, and other asset details.
//             </Typography>
//             <form onSubmit={formik.handleSubmit}>
//                 <Grid container spacing={3}>
//                 {/* Title Field */}
//                 <Grid item xs={12}>
//                     <TextField
//                     fullWidth
//                     size="small"
//                     label="Asset Name"
//                     variant="outlined"
//                     name="name"
//                     value={formik.values.name}
//                     onChange={formik.handleChange}
//                     onBlur={formik.handleBlur}
//                     error={formik.touched.name && Boolean(formik.errors.name)}
//                     helperText={formik.touched.name && formik.errors.name}
//                     InputLabelProps={{
//                         sx: {
//                         color: "#555", // Default label color
//                         fontSize: 10,
//                         transition: "color 0.3s ease",
//                         "&:hover": {
//                             color: "#333"
//                         }, // Label color on hover
//                         "&.Mui-focused": { 
//                             color: "#001f3f",
//                             textTransform:"uppercase",
//                             fontWeight:500
//                         }, // Label color on focus
//                         },
//                     }}
//                     inputProps={{
//                         sx: {
//                         fontSize: 10, // Field content size
//                         color: "#333", // Field content color
//                         "&::placeholder": {
//                             color: "#aaa", // Placeholder color
//                             fontSize: 10,
//                         },
//                         },
//                     }}
//                     sx={{
//                         '& .MuiOutlinedInput-root': {
//                         borderRadius: "2px",
//                         "&.Mui-focused fieldset": { 
//                             borderColor: "#001f3f",
//                             borderRadius:"2px"
//                         }, // Focus border
//                         },
//                         '& .MuiFormHelperText-root': {
//                         fontSize: 9, // Smaller font size for validation errors
//                         color: 'error.main', // Red color for errors
//                         textTransform:"capitalize"
//                         },
                    
//                     }}
//                     />
//                 </Grid>
    
//                 {/* Content Field */}
//                 <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   multiline
//                   size="small"
//                   rows={4}
//                   label="Description"
//                   name="description"
//                   value={formik.values.description}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                   error={formik.touched.description && Boolean(formik.errors.description)}
//                   helperText={formik.touched.description && formik.errors.description}
//                     InputLabelProps={{
//                         sx: {
//                         color: "#555", // Default label color
//                         fontSize: 10,
//                         transition: "color 0.3s ease",
//                         "&:hover": {
//                             color: "#333"
//                         }, // Label color on hover
//                         "&.Mui-focused": { 
//                             color: "#001f3f",
//                             textTransform:"uppercase",
//                             fontWeight:500
//                         }, // Label color on focus
//                         },
//                     }}
//                     inputProps={{
//                         sx: {
//                         fontSize: 10, // Field content size
//                         color: "#333", // Field content color
//                         "&::placeholder": {
//                             color: "#aaa", // Placeholder color
//                             fontSize: 10,
//                         },
//                         },
//                     }}
//                     sx={{
//                         '& .MuiOutlinedInput-root': {
//                             borderRadius: "2px",
//                             "&.Mui-focused fieldset": {
//                                 borderColor: "#001f3f",
//                                 borderRadius: "2px"
//                             }
//                         },
//                         '& .MuiFormHelperText-root': {
//                             fontSize: 9,
//                             color: 'error.main',
//                             textTransform: "capitalize"
//                         }
//                     }}
                   
//                />
//                 </Grid>

//                 <Grid item xs={12}>
//                     <TextField
//                     fullWidth
//                     size="small"
//                     type="number"
//                     InputProps={{
//                       startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
//                     }}
//                     label="Price"
//                     name="price"
//                     value={formik.values.price}
//                     onChange={formik.handleChange}
//                     onBlur={formik.handleBlur}
//                     error={formik.touched.price && Boolean(formik.errors.price)}
//                     helperText={formik.touched.price && formik.errors.price}
//                     InputLabelProps={{
//                         sx: {
//                         color: "#555", // Default label color
//                         fontSize: 10,
//                         transition: "color 0.3s ease",
//                         "&:hover": {
//                             color: "#333"
//                         }, // Label color on hover
//                         "&.Mui-focused": { 
//                             color: "#001f3f",
//                             textTransform:"uppercase",
//                             fontWeight:500
//                         }, // Label color on focus
//                         },
//                     }}
//                     inputProps={{
//                         sx: {
//                         fontSize: 10, // Field content size
//                         color: "#333", // Field content color
//                         "&::placeholder": {
//                             color: "#aaa", // Placeholder color
//                             fontSize: 10,
//                         },
//                         },
//                     }}
//                     sx={{
//                         '& .MuiOutlinedInput-root': {
//                         borderRadius: "2px",
//                         "&.Mui-focused fieldset": { 
//                             borderColor: "#001f3f",
//                             borderRadius:"2px"
//                         }, // Focus border
//                         },
//                         '& .MuiFormHelperText-root': {
//                         fontSize: 9, // Smaller font size for validation errors
//                         color: 'error.main', // Red color for errors
//                         textTransform:"capitalize"
//                         },
                    
//                     }}
//                     />
//                 </Grid>

//                 <Grid item xs={12}>
//                     <TextField
//                     fullWidth
//                     size="small" 
//                     label="Stock"
//                     name="stock"
//                     type="number"
//                     value={formik.values.stock}
//                     onChange={formik.handleChange}
//                     onBlur={formik.handleBlur}
//                     error={formik.touched.stock && Boolean(formik.errors.stock)}
//                     helperText={formik.touched.stock && formik.errors.stock}
//                     InputLabelProps={{
//                         sx: {
//                         color: "#555", // Default label color
//                         fontSize: 10,
//                         transition: "color 0.3s ease",
//                         "&:hover": {
//                             color: "#333"
//                         }, // Label color on hover
//                         "&.Mui-focused": { 
//                             color: "#001f3f",
//                             textTransform:"uppercase",
//                             fontWeight:500
//                         }, // Label color on focus
//                         },
//                     }}
//                     inputProps={{
//                         sx: {
//                         fontSize: 10, // Field content size
//                         color: "#333", // Field content color
//                         "&::placeholder": {
//                             color: "#aaa", // Placeholder color
//                             fontSize: 10,
//                         },
//                         },
//                     }}
//                     sx={{
//                         '& .MuiOutlinedInput-root': {
//                         borderRadius: "2px",
//                         "&.Mui-focused fieldset": { 
//                             borderColor: "#001f3f",
//                             borderRadius:"2px"
//                         }, // Focus border
//                         },
//                         '& .MuiFormHelperText-root': {
//                         fontSize: 9, // Smaller font size for validation errors
//                         color: 'error.main', // Red color for errors
//                         textTransform:"capitalize"
//                         },
                    
//                     }}
//                     />
//                 </Grid> 
    
//                 {/* Image Upload Field */}
//                 <Grid item xs={12}>
//                     <Button
//                     variant="contained"
//                     component="label"
//                     sx={{
//                         color:"black",
//                         fontSize:10,
//                         fontWeight:500,
//                         borderRadius: '2px',
//                         textTransform: 'none',
//                         boxShadow:"none",
//                         py: 1,
//                         px: 3,
//                         border:"1px solid #ccc",
//                         // background:"none"
//                         backgroundColor:"#fff",                     
//                         "&:hover": {
//                             boxShadow: "none",
//                         },
//                     }}
//                     >
//                         <Box sx={{
//                             alignItems:"center",
//                             justifyContent:"center",
//                             display:"flex",
//                             flexDirection:"column"
//                         }}>
//                             <CloudUpload color="gray" />
//                             <Typography sx={{
//                                 fontSize:10,
//                                 fontWeight:500,
//                                 textTransform:"capitalize"
//                             }}>Click to upload</Typography>
//                             <Typography sx={{
//                                 fontSize:10,
//                                 fontWeight:400,
//                                 color:"#bcbcbc"
//                             }}>JPEG, PNG or GIF</Typography>
//                             <input
//                                 type="file"
//                                 hidden
//                                 name="image"
//                                 onChange={(event) => {
//                                 formik.setFieldValue('image', event.currentTarget.files[0]);
//                                 }}
//                                 onBlur={formik.handleBlur}
//                                 accept="image/*"
//                             />
//                         </Box>
                    
//                     </Button>
//                     {formik.touched.image && formik.errors.image && (
//                     <Typography variant="body2" color="error" sx={{ mt: 1, fontSize:10, textTransform:"capitalize" }}>
//                         {formik.errors.image}
//                     </Typography>
//                     )}
//                     {formik.values.image && (
//                     <Box sx={{ mt: 2 }}>
//                         <Typography variant="body2" sx={{ mb: 1, fontSize:10, }}>
//                         SELECTED FILE: {formik.values.image.name}
//                         </Typography>
//                         <img
//                         src={URL.createObjectURL(formik.values.image)}
//                         alt="Preview"
//                         style={{
//                             maxWidth: '100%',
//                             height: 'auto',
//                             maxHeight:400,
//                             borderRadius: '2px',
//                             boxShadow: 0,
//                         }}
//                         />
//                     </Box>
//                     )}
//                 </Grid>
    
//                 {/* Submit Button */}
//                 <Grid item xs={12}>
//                     <Button
//                     type="submit"
//                     variant="contained"
//                     fullWidth
//                     size="large"
//                     sx={{
//                         borderRadius: "2px",
//                         textTransform: 'none',
//                         py: 1.5,
//                         fontSize: 10,
//                         boxShadow:0,
//                         backgroundColor:"#001f3f",                  
//                         "&:hover": {
//                             boxShadow: "none",
//                         },
//                     }}
//                     >
//                     Add New Asset
//                     </Button>
//                 </Grid>
//                 </Grid>
//             </form>
//             </Box>
//           </Container>
//                         ):open.update ?(
//                           <Container maxWidth="md">
//                             <IconButton
//                                 onClick={toggleDrawer("create", false)}
//                             >
//                                 <ArrowBack/>
//                             </IconButton>
//                           <Box
//                               sx={{
//                                   mt: "4px",
//                                   p: "4px",
//                                   // borderRadius: 2,
//                                   boxShadow: 0,
//                                   backgroundColor: 'background.paper',
//                               }}
//                               >
//                               <Typography variant="h4" sx={{ fontWeight: 700, color: "#001f3f", fontSize:12, textTransform:"uppercase" }}>
//                                   Update Asset
//                               </Typography>
//                               <Typography variant="body1" sx={{mb:2, color: "text.secondary", fontSize: 10, textTransform:"capitalize" }}>
//                                   Fill in the details below to update asset, including name, image, and other asset details.
//                               </Typography>
//                               <form onSubmit={upformik.handleSubmit}>
//                                   <Grid container spacing={3}>
//                                   {/* Title Field */}
//                                   <Grid item xs={12}>
//                                       <TextField
//                                       fullWidth
//                                       size="small"
//                                       label="Asset Name"
//                                       variant="outlined"
//                                       name="name"
//                                       value={upformik.values.name}
//                                       onChange={upformik.handleChange}
//                                       onBlur={upformik.handleBlur}
//                                       error={upformik.touched.name && Boolean(upformik.errors.name)}
//                                       helperText={upformik.touched.name && upformik.errors.name}
//                                       InputLabelProps={{
//                                           sx: {
//                                           color: "#555", // Default label color
//                                           fontSize: 10,
//                                           transition: "color 0.3s ease",
//                                           "&:hover": {
//                                               color: "#333"
//                                           }, // Label color on hover
//                                           "&.Mui-focused": { 
//                                               color: "#001f3f",
//                                               textTransform:"uppercase",
//                                               fontWeight:500
//                                           }, // Label color on focus
//                                           },
//                                       }}
//                                       inputProps={{
//                                           sx: {
//                                           fontSize: 10, // Field content size
//                                           color: "#333", // Field content color
//                                           "&::placeholder": {
//                                               color: "#aaa", // Placeholder color
//                                               fontSize: 10,
//                                           },
//                                           },
//                                       }}
//                                       sx={{
//                                           '& .MuiOutlinedInput-root': {
//                                           borderRadius: "2px",
//                                           "&.Mui-focused fieldset": { 
//                                               borderColor: "#001f3f",
//                                               borderRadius:"2px"
//                                           }, // Focus border
//                                           },
//                                           '& .MuiFormHelperText-root': {
//                                           fontSize: 9, // Smaller font size for validation errors
//                                           color: 'error.main', // Red color for errors
//                                           textTransform:"capitalize"
//                                           },
                                      
//                                       }}
//                                       />
//                                   </Grid>
                      
//                                   {/* Content Field */}
//                                   <Grid item xs={12}>
//                                   <TextField           
//                                       fullWidth
//                                       multiline
//                                       size="small"
//                                       rows={4}
//                                       label="Description"
//                                       name="description"
//                                       value={upformik.values.description}
//                                       onChange={upformik.handleChange}
//                                       onBlur={upformik.handleBlur}
//                                       error={upformik.touched.description && Boolean(upformik.errors.description)}
//                                       helperText={upformik.touched.description && upformik.errors.description} 
//                                       InputLabelProps={{
//                                           sx: {
//                                           color: "#555", // Default label color
//                                           fontSize: 10,
//                                           transition: "color 0.3s ease",
//                                           "&:hover": {
//                                               color: "#333"
//                                           }, // Label color on hover
//                                           "&.Mui-focused": { 
//                                               color: "#001f3f",
//                                               textTransform:"uppercase",
//                                               fontWeight:500
//                                           }, // Label color on focus
//                                           },
//                                       }}
//                                       inputProps={{
//                                           sx: {
//                                           fontSize: 10, // Field content size
//                                           color: "#333", // Field content color
//                                           "&::placeholder": {
//                                               color: "#aaa", // Placeholder color
//                                               fontSize: 10,
//                                           },
//                                           },
//                                       }}
//                                       sx={{
//                                           '& .MuiOutlinedInput-root': {
//                                               borderRadius: "2px",
//                                               "&.Mui-focused fieldset": {
//                                                   borderColor: "#001f3f",
//                                                   borderRadius: "2px"
//                                               }
//                                           },
//                                           '& .MuiFormHelperText-root': {
//                                               fontSize: 9,
//                                               color: 'error.main',
//                                               textTransform: "capitalize"
//                                           }
//                                       }}
//                                   />
//                                   </Grid>
                
//                                   <Grid item xs={12}>
//                                       <TextField
//                                       fullWidth
//                                       size="small"
//                                       label="Price"
//                                       name="price"
//                                       type="number"
//                                       InputProps={{
//                                         startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
//                                       }}
//                                       value={upformik.values.price}
//                                       onChange={upformik.handleChange}
//                                       onBlur={upformik.handleBlur}
//                                       error={upformik.touched.price && Boolean(upformik.errors.price)}
//                                       helperText={upformik.touched.price && upformik.errors.price}
//                                       InputLabelProps={{
//                                           sx: {
//                                           color: "#555", // Default label color
//                                           fontSize: 10,
//                                           transition: "color 0.3s ease",
//                                           "&:hover": {
//                                               color: "#333"
//                                           }, // Label color on hover
//                                           "&.Mui-focused": { 
//                                               color: "#001f3f",
//                                               textTransform:"uppercase",
//                                               fontWeight:500
//                                           }, // Label color on focus
//                                           },
//                                       }}
//                                       inputProps={{
//                                           sx: {
//                                           fontSize: 10, // Field content size
//                                           color: "#333", // Field content color
//                                           "&::placeholder": {
//                                               color: "#aaa", // Placeholder color
//                                               fontSize: 10,
//                                           },
//                                           },
//                                       }}
//                                       sx={{
//                                           '& .MuiOutlinedInput-root': {
//                                           borderRadius: "2px",
//                                           "&.Mui-focused fieldset": { 
//                                               borderColor: "#001f3f",
//                                               borderRadius:"2px"
//                                           }, // Focus border
//                                           },
//                                           '& .MuiFormHelperText-root': {
//                                           fontSize: 9, // Smaller font size for validation errors
//                                           color: 'error.main', // Red color for errors
//                                           textTransform:"capitalize"
//                                           },
                                      
//                                       }}
//                                       />
//                                   </Grid>
                
//                                   <Grid item xs={12}>
//                                       <TextField
//                                       fullWidth
//                                       size="small"
//                                       label="Stock"
//                                       name="stock"
//                                       type="number"
//                                       value={upformik.values.stock}
//                                       onChange={upformik.handleChange}
//                                       onBlur={upformik.handleBlur}
//                                       error={upformik.touched.stock && Boolean(upformik.errors.stock)}
//                                       helperText={upformik.touched.stock && upformik.errors.stock}
//                                       InputLabelProps={{
//                                           sx: {
//                                           color: "#555", // Default label color
//                                           fontSize: 10,
//                                           transition: "color 0.3s ease",
//                                           "&:hover": {
//                                               color: "#333"
//                                           }, // Label color on hover
//                                           "&.Mui-focused": { 
//                                               color: "#001f3f",
//                                               textTransform:"uppercase",
//                                               fontWeight:500
//                                           }, // Label color on focus
//                                           },
//                                       }}
//                                       inputProps={{
//                                           sx: {
//                                           fontSize: 10, // Field content size
//                                           color: "#333", // Field content color
//                                           "&::placeholder": {
//                                               color: "#aaa", // Placeholder color
//                                               fontSize: 10,
//                                           },
//                                           },
//                                       }}
//                                       sx={{
//                                           '& .MuiOutlinedInput-root': {
//                                           borderRadius: "2px",
//                                           "&.Mui-focused fieldset": { 
//                                               borderColor: "#001f3f",
//                                               borderRadius:"2px"
//                                           }, // Focus border
//                                           },
//                                           '& .MuiFormHelperText-root': {
//                                           fontSize: 9, // Smaller font size for validation errors
//                                           color: 'error.main', // Red color for errors
//                                           textTransform:"capitalize"
//                                           },
                                      
//                                       }}
//                                       />
//                                   </Grid> 
                                   
                      
//                                   {/* Image Upload Field */}
//                                   <Grid item xs={12}>
//                                       <Button
//                                       variant="contained"
//                                       component="label"
//                                       sx={{
//                                           color:"black",
//                                           fontSize:10,
//                                           fontWeight:500,
//                                           borderRadius: '2px',
//                                           textTransform: 'none',
//                                           boxShadow:"none",
//                                           py: 1,
//                                           px: 3,
//                                           border:"1px solid #ccc",
//                                           // background:"none"
//                                           backgroundColor:"#fff",                     
//                                           "&:hover": {
//                                               boxShadow: "none",
//                                           },
//                                       }}
//                                       >
//                                           <Box sx={{
//                                               alignItems:"center",
//                                               justifyContent:"center",
//                                               display:"flex",
//                                               flexDirection:"column"
//                                           }}>
//                                               <CloudUpload color="gray" />
//                                               <Typography sx={{
//                                                   fontSize:10,
//                                                   fontWeight:500,
//                                                   textTransform:"capitalize"
//                                               }}>Click to upload</Typography>
//                                               <Typography sx={{
//                                                   fontSize:10,
//                                                   fontWeight:400,
//                                                   color:"#bcbcbc"
//                                               }}>JPEG, PNG or GIF</Typography>
//                                               <input
//                                                   type="file"
//                                                   hidden
//                                                   name="image"
//                                                   onChange={(event) => {
//                                                     upformik.setFieldValue('image', event.currentTarget.files[0]);
//                                                   }}
//                                                   onBlur={upformik.handleBlur}
//                                                   accept="image/*"
//                                               />
//                                           </Box>
                                      
//                                       </Button>
//                                       {/* {formik.touched.asset_image && formik.errors.asset_image && (
//                                       <Typography variant="body2" color="error" sx={{ mt: 1, fontSize:10, textTransform:"capitalize" }}>
//                                           {formik.errors.asset_image}
//                                       </Typography>
//                                       )} */}
//                                       {upformik.values.image && (
//                                       <Box sx={{ mt: 2 }}>
//                                           <Typography variant="body2" sx={{ mb: 1, fontSize:10, }}>
//                                           SELECTED FILE: {upformik.values.image.name}
//                                           </Typography>
//                                           <img
//                                           src={URL.createObjectURL(upformik.values.image)}
//                                           alt="Preview"
//                                           style={{
//                                               maxWidth: '100%',
//                                               height: 'auto',
//                                               maxHeight:400,
//                                               borderRadius: '2px',
//                                               boxShadow: 0,
//                                           }}
//                                           />
//                                       </Box>
//                                       )}
//                                   </Grid>
                      
//                                   {/* Submit Button */}
//                                   <Grid item xs={12}>
//                                       <Button
//                                       type="submit"
//                                       variant="contained"
//                                       fullWidth
//                                       size="large"
//                                       sx={{
//                                           borderRadius: "2px",
//                                           textTransform: 'none',
//                                           py: 1.5,
//                                           fontSize: 10,
//                                           boxShadow:0,
//                                           backgroundColor:"#001f3f",                  
//                                           "&:hover": {
//                                               boxShadow: "none",
//                                           },
//                                       }}
//                                       >
//                                       Update Asset
//                                       </Button>
//                                   </Grid>
//                                   </Grid>
//                               </form>
//                               </Box>
//                       </Container>
//                         ):(
//                             <Container maxWidth='xl'>
//                                 <Typography variant="h2" component="h2" 
//                                 sx={{ 
//                                     fontSize:12,
//                                     fontWeight:700,
//                                     textTransform:"uppercase"
//                                 }}
//                                 >
//                                     Urithi dummy assets Management
//                                 </Typography>
//                                 <Typography variant="body1" component="h2" 
//                                 sx={{ 
//                                     marginBottom: 2,
//                                     color: "text.secondary",
//                                     textTransform:"capitalize",
//                                     fontSize:10
//                                 }}>
//                                     Manage and Publish urithi Tradable Products
//                                 </Typography>
//                                 <Button
//                                 startIcon={<Add/>}
//                                 variant="contained"
//                                 // color="primary"
//                                 elevation={0}
//                                 disableElevation={true}
//                                 onClick={toggleDrawer("create", true)}
//                                 sx={{ 
//                                     mb: 1,
//                                     color:"#001f3f",
//                                     borderRadius:"1px",
//                                     fontSize:10,
//                                     fontWeight:500,
//                                     textTransform:"capitalize",
//                                     background:"none",
//                                     textDecoration:"underline"
//                                 }}
//                                 >
                                
//                                 Click here to Add New Asset
//                                 </Button>
//                                 <Typography variant="h4" component="h4" 
//                                 sx={{ 
//                                     mt:2,mb:1,
//                                     fontSize:10,
//                                     textTransform:"uppercase",
//                                     fontWeight:600
                                    
//                                     }}>
//                                     Assets List
//                                 </Typography>
//                                 {/* Responsive Search and Sort Controls */}
//                                 <Grid container spacing={2} alignItems="center">
//                                     <Grid item xs={12} sm={8}>
//                                         <TextField
//                                             label="Search by Asset Name.."
//                                             variant="outlined"
//                                             size="small"
//                                             fullWidth
//                                             InputLabelProps={{
//                                             sx: {
//                                                 color: "#555", // Default label color
//                                                 fontSize: 10,
//                                                 transition: "color 0.3s ease",
//                                                 "&:hover": {
//                                                     color: "#333"
//                                                 }, // Label color on hover
//                                                 "&.Mui-focused": { 
//                                                     color: "#001f3f",
//                                                     textTransform:"uppercase",
//                                                     fontWeight:500
//                                                 }, // Label color on focus
//                                             },
//                                             }}
//                                             inputProps={{
//                                             sx: {
//                                                 fontSize: 10, // Field content size
//                                                 color: "#333", // Field content color
//                                                 "&::placeholder": {
//                                                 color: "#aaa", // Placeholder color
//                                                 fontSize: 10,
//                                                 },
//                                             },
//                                             }}
//                                             sx={{
//                                             backgroundColor: "#e8e8e8",
//                                             borderRadius: "1px",
//                                             "& .MuiOutlinedInput-root": {
//                                                 "& fieldset": { 
//                                                     borderColor: "#ccc",  
//                                                     borderRadius:"2px"
//                                                 }, // Default border
//                                                 "&:hover fieldset": { 
//                                                     borderColor: "#0000",
//                                                     borderRadius:"2px"
//                                                 }, // Hover border
//                                                 "&.Mui-focused fieldset": { 
//                                                     borderColor: "#001f3f",
//                                                     borderRadius:"2px"
//                                                 }, // Focus border
//                                             },
//                                             }}
//                                             value={searchQuery}
//                                             onChange={(e) => setSearchQuery(e.target.value)}
//                                         />
//                                     </Grid>
//                                     <Grid item xs={12} sm={4}>
//                                         <FormControl
//                                         size="small"
//                                         fullWidth
                                        
//                                         sx={{
//                                         backgroundColor: "#e8e8e8",
//                                         borderRadius: "1px",
//                                         "& .MuiOutlinedInput-root": {
//                                             "& fieldset": { 
//                                                 borderColor: "#ccc",  
//                                                 borderRadius:"2px"
//                                             }, // Default border
//                                             "&:hover fieldset": { 
//                                                 borderColor: "#0000",
//                                                 borderRadius:"2px"
//                                             }, // Hover border
//                                             "&.Mui-focused fieldset": { 
//                                                 borderColor: "#001f3f",
//                                                 borderRadius:"2px"
//                                             }, // Focus border
//                                         },
//                                         }}
//                                         >
//                                             <InputLabel 
//                                             sx={{
//                                                 color: "#555", // Default color
//                                                 fontSize: 10, // Default font size
//                                                 transition: "color 0.3s ease, font-size 0.3s ease",
//                                                 "&:hover": {
//                                                 color: "#333", // Color on hover
//                                                 },
//                                                 "&.Mui-focused": { 
//                                                 color: "#001f3f", // Color on focus
//                                                 textTransform: "uppercase",
//                                                 fontWeight: 500,
//                                                 fontSize: 10, // Font size on focus
//                                                 },
//                                             }}>Sort By</InputLabel>
//                                             <Select
//                                             value={sortBy}
//                                             onChange={(e) => setSortBy(e.target.value)}
//                                             label="Sort By"
//                                             InputLabelProps={{
//                                             sx: {
//                                                 color: "#555", // Default label color
//                                                 fontSize: 10,
//                                                 transition: "color 0.3s ease",
//                                                 "&:hover": {
//                                                     color: "#333"
//                                                 }, // Label color on hover
//                                                 "&.Mui-focused": { 
//                                                     color: "#001f3f",
//                                                     textTransform:"uppercase",
//                                                     fontWeight:500
//                                                 }, // Label color on focus
//                                             },
//                                             }}
//                                             MenuProps={{
//                                                 PaperProps: {
//                                                 sx: {
//                                                     boxShadow: "none", // Removes box shadow
//                                                     elevation: 0, // Ensures no elevation
//                                                     border: "1px solid #ccc", // Optional: Adds a subtle border for separation
//                                                     backgroundColor: "#fff", // Ensures a clean white background
//                                                     mt: 1.5, 
//                                                     borderRadius:"1px"
//                                                 },
//                                                 },
//                                             }}
//                                             inputProps={{
//                                             sx: {
//                                                 fontSize: 10, // Field content size
//                                                 color: "#333", // Field content color
//                                                 "&::placeholder": {
//                                                 color: "#aaa", // Placeholder color
//                                                 fontSize: 10,
//                                                 },
//                                             },
//                                             }}
//                                             >
//                                                 <MenuItem value="date" 
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                         fontWeight:500,
//                                                         "&.Mui-selected": { 
//                                                             backgroundColor: "#001f3f !important", // Active item color (Red)
//                                                             color: "white",
//                                                         },
//                                                         "&:hover": {
//                                                             backgroundColor: "#CCDDEE", // Lighter red on hover
//                                                         },
//                                                     }}
//                                                 >
//                                                     Published Date
//                                                 </MenuItem>
//                                                 <MenuItem value="name"
            
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                         fontWeight:500,
//                                                         "&.Mui-selected": { 
//                                                             backgroundColor: "#001f3f !important", // Active item color (Red)
//                                                             color: "white",
//                                                         },
//                                                         "&:hover": {
//                                                             backgroundColor: "#CCDDEE", // Lighter red on hover
//                                                         },
//                                                     }}
//                                                 >
//                                                     Asset name
//                                                 </MenuItem>
//                                             </Select>
//                                         </FormControl>
//                                     </Grid>
//                                 </Grid>
//                                 <TableContainer component={Paper} elevation={0}>
//                                     <Table>
//                                         <TableHead>
//                                             <TableRow>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>Asset Name</TableCell>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>stock</TableCell>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>Asset Image</TableCell>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>Price (TZS)</TableCell>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>
//                                                   Description
//                                                 </TableCell>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>Published At</TableCell>
//                                                 <TableCell sx={{
//                                                     fontSize:8,
//                                                     fontWeight:600,
//                                                     textTransform:"uppercase"
//                                                 }}>Actions</TableCell>
//                                             </TableRow>
//                                         </TableHead>
//                                         <TableBody>
//                                         {sortedNews.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset) => (
//                                                 <TableRow key={asset.id}>
//                                                 <TableCell>
//                                                     <Typography 
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                     }} variant="body1" fontWeight="bold">
//                                                         {asset.name}
//                                                     </Typography>
//                                                 </TableCell>
//                                                 <TableCell>
//                                                     <Typography 
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                     }} variant="body1" fontWeight="bold">
//                                                         {asset.stock}
//                                                     </Typography>
//                                                 </TableCell>
//                                                 <TableCell>
//                                                     <Avatar
//                                                     src={`${IMG_BASE_URL}${asset.image}`}  // Make sure asset_image returns a valid URL
//                                                     alt={asset.asset_name}
//                                                     style={{ width: 50, height: 50 }}
//                                                     />
//                                                 </TableCell>
                                                
//                                                 <TableCell>
//                                                 <Typography 
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                     }} variant="body1" fontWeight="bold">
//                                                        {asset.price}
//                                                     </Typography>
//                                                     </TableCell>
//                                                 <TableCell>
//                                                 <Typography 
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                     }} variant="body1" fontWeight="bold">
//                                                         {asset.description}
//                                                     </Typography>
//                                                     </TableCell>
//                                                 <TableCell>
//                                                 <Typography 
//                                                     sx={{
//                                                         fontSize:10,
//                                                         textTransform:"capitalize",
//                                                     }} variant="body1" fontWeight="bold">
//                                                        {new Date(asset.published_at).toLocaleDateString()}
//                                                     </Typography>
//                                                 </TableCell>
//                                                 <TableCell>
//                                                     <Box  sx={{
//                                                     display:"flex",
//                                                     flexDirection:"row",
//                                                     gap:"10px"
//                                                 }}>
//                                                         <IconButton onClick={() => handleOpenUpdateDrawer(asset)} color="inherit">
//                                                             <Edit/> 
//                                                         </IconButton>
//                                                         <IconButton
//                                                         color='error'
//                                                         onClick={()=>handleDelete(asset.id)} 
//                                                         disabled={deleteMutation.isLoading}
//                                                         >
//                                                             <Delete/> 
//                                                         </IconButton>
//                                                 </Box>
//                                                 </TableCell>
//                                             </TableRow>
//                                             ))}
//                                             </TableBody>
//                                     </Table>
//                                 </TableContainer>
//                                 <TablePagination
//                                 rowsPerPageOptions={[5, 10, 25]}
//                                 component="div"
//                                 count={sortedNews.length}
//                                 rowsPerPage={rowsPerPage}
//                                 page={page}
//                                 onPageChange={handleChangePage}
//                                 onRowsPerPageChange={handleChangeRowsPerPage}
//                                 sx={{
//                                     "& .MuiTablePagination-root": {
//                                         fontSize: 10, // Adjust overall pagination text size
//                                         fontWeight: 500,
//                                         // textTransform: "uppercase",
//                                     },
//                                     "& .MuiTablePagination-toolbar": {
//                                         fontSize: 10, // Adjust toolbar text size (e.g., "Rows per page")
//                                         // textTransform:"uppercase"
//                                     },
//                                     "& .MuiTablePagination-selectLabel": {
//                                         fontSize: 10, // Adjust "Rows per page" text size
//                                     },
//                                     "& .MuiTablePagination-input": {
//                                         fontSize: 10, // Adjust dropdown text size
//                                     },
//                                     "& .MuiTablePagination-actions button": {
//                                         fontSize: 10, // Adjust button (next/prev) text size
//                                     },
//                                     "& .MuiSelect-select": {
//                                         backgroundColor: "#f5f5f5", // Light gray dropdown background
//                                         borderRadius: "1px",
//                                         padding: "5px 10px",
//                                         fontSize: 10, // Adjust dropdown font size
//                                     },
//                                     "& .MuiTablePagination-displayedRows": {
//                                         fontSize: 10, // Adjust font size of "1–1 of 1"
//                                         fontWeight: 500,
//                                         // textTransform: "uppercase",
//                                     },
//                                 }}
//                                 SelectProps={{
//                                     MenuProps: {
//                                         PaperProps: {
//                                             sx: {
//                                                 borderRadius: "1px", // Rounded border for dropdown
//                                                 backgroundColor: "#fff", // Background color of dropdown
//                                                 elevation:0,
//                                                 boxShadow:"none",
//                                                 border: "1px solid #ccc", // Optional: Adds a subtle border for separation
//                                                 mt: 1.5, 
//                                                 "& .MuiMenuItem-root": {
//                                                     fontSize: 10, // Dropdown text size
//                                                     fontWeight: 500, // Dropdown text weight
//                                                     "&.Mui-selected": {
//                                                         backgroundColor: "#001f3f", // Active dropdown item color (red)
//                                                         color: "white",
//                                                     },
//                                                     "&:hover": {
//                                                         backgroundColor: "#CCDDEE", // Hover color (lighter red)
//                                                     },
//                                                 },
//                                             },
//                                         },
//                                     },
//                                 }}
//                             />
//                             </Container>
//                         )
//                     )}

//       {/* Toast Notifications */}
//       <Snackbar
//       open={toast.open}
//       autoHideDuration={3000}
//       onClose={() => setToast({ open: false, message: "", severity: "" })}
//       >
//       <Alert severity={toast.severity} 
//       sx={{
//               fontSize: 10, // Adjust font size
//               fontWeight:500, // Make text bold
//               width: "100%",
//               borderRadius: "1px", // Rounded corners
//               padding: "10px", // Adjust padding 
//               textTransform:"capitalize"
//           }}
//       >
//           {toast.message}
//       </Alert>
//       </Snackbar>
//     </Container>
//   )
// }

// export default UrithiDummyAssets
